import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getCsrfValue } from "~/common/utils/ApiHelper";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { configuratorConfigurationKey, configuratorMutationKey } from "../data/configuratorConfigurationQuery";
import type { ConfiguratorConfiguration } from "../contract/configuration";
import { withConfiguratorMutationLock } from "./configuratorMutationLock";

export const useTemporaryConfiguratorReset = (configuration: ConfiguratorConfiguration | null) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: configuration ? configuratorMutationKey(configuration.productCode) : ["configurator-mutation"],
    mutationFn: async () => {
      if (!configuration) {
        throw new Error("Cannot reset an unavailable configuration.");
      }

      return withConfiguratorMutationLock(configuration.configId, async () => {
        const current = queryClient.getQueryData<{ configuration: ConfiguratorConfiguration }>(
          configuratorConfigurationKey(configuration.productCode),
        );
        if (current && current.configuration.configId !== configuration.configId) {
          throw new Error("Configurator reset belongs to a superseded configuration.");
        }

        const response = await fetch(resolveApiUrl("/cpq/v2/reset"), {
          body: JSON.stringify({ configId: configuration.configId }),
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            CSRFToken: getCsrfValue(),
          },
          method: "POST",
        });

        if (!response.ok) {
          throw new Error(`Configurator reset failed with status ${response.status}.`);
        }
      });
    },
    onSuccess: async () => {
      if (configuration) {
        await queryClient.invalidateQueries({
          queryKey: configuratorConfigurationKey(configuration.productCode),
        });
      }
    },
  });

  return {
    isResetting: mutation.isPending,
    resetConfiguration: mutation.mutateAsync,
  };
};
